import React from 'react';
import { Chip, makeStyles, Theme, useTheme, CircularProgress } from '@bb-ui/react-library';
import clsx from 'clsx';
import { CohortStatus } from 'App.types';
import { getCohortPhase, getCohortSubStatus, normalizeCohortStatus } from '../utils';

export interface StatusChipProps {
  status: CohortStatus | null | undefined;
  enableNotifications?: boolean;
  size?: 'small' | 'medium';
  className?: string;
  loading?: boolean;
}

const useStyles = makeStyles(() => ({
  chip: {
    minHeight: 32,
    padding: '6px 14px',
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: '0.875rem',
    width: 'auto',
    maxWidth: '100%',
    display: 'inline-flex',
    whiteSpace: 'normal',
    height: 'auto',
  },
  loadingChip: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    whiteSpace: 'nowrap',
  },
  spinner: {
    color: 'inherit',
  },
}));

const getStatusStyles = (
  status: CohortStatus | null | undefined,
  enableNotifications: boolean,
  theme: Theme,
) => {
  const normalizedStatus = normalizeCohortStatus(status, enableNotifications);
  const phase = getCohortPhase(normalizedStatus, enableNotifications);
  const subStatus = getCohortSubStatus(normalizedStatus, enableNotifications);

  if (subStatus === 'error') {
    return { bg: theme.palette.indicatorSecondary.main, fg: theme.palette.common.white };
  }

  if (phase === 'completed') {
    return { bg: theme.palette.success.main, fg: theme.palette.common.white };
  }

  if (phase === 'notification') {
    if (subStatus === 'planned') {
      return { bg: theme.palette.background.b6, fg: theme.palette.common.white };
    }
    if (subStatus === 'pending_confirmation') {
      return { bg: theme.palette.warning.main, fg: theme.palette.common.white };
    }
    if (subStatus === 'confirmed') {
      return { bg: theme.palette.brand.renew, fg: theme.palette.common.white };
    }
  }

  if (phase === 'awaiting' || subStatus === 'in_progress') {
    return { bg: theme.palette.indicatorPrimary.main, fg: theme.palette.common.white };
  }

  return { bg: theme.palette.background.b6, fg: theme.palette.common.white };
};

const StatusChip: React.FC<StatusChipProps> = ({
  status,
  enableNotifications = true,
  size = 'small',
  className,
  loading = false,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const { bg, fg } = getStatusStyles(status, enableNotifications, theme);
  const displayStatus = normalizeCohortStatus(status, enableNotifications);

  return (
    <Chip
      label={
        loading ? (
          <div className={classes.loadingChip}>
            <CircularProgress size={16} className={classes.spinner} label="" id="status-loading" />
          </div>
        ) : (
          displayStatus
        )
      }
      size={size}
      className={clsx(classes.chip, className)}
      style={{
        backgroundColor: loading ? theme.palette.background.b3 : bg,
        color: fg,
      }}
    />
  );
};

export default StatusChip;
